import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
  CollectionDetail,
  CollectionShipment,
} from 'services/CollectionsService';

const STATUS_LABELS: Record<string, string> = {
  pending_collection: 'Pendiente',
  in_transit_to_store: 'En transito',
  collected: 'Colectado',
  pending_intake: 'Pendiente de ingreso',
  received: 'Recibida',
  cancelled: 'Cancelada',
  in_progress: 'En curso',
  completed: 'Completada',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  arrived: 'Llegada',
  enter: 'Llegada',
  departed: 'Salida',
  exit: 'Salida',
  leave: 'Salida',
};

function fmt(dateStr: string): string {
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtShort(dateStr: string): string {
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function geoText(
  lat: number | null | undefined,
  lng: number | null | undefined,
): string {
  if (!lat || !lng) return '—';
  return `${lat.toFixed(5)}; ${lng.toFixed(5)}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function geoLinkHook(doc: jsPDF, geoColIndex: number): (data: any) => void {
  return (data) => {
    if (data.section !== 'body' || data.column.index !== geoColIndex) return;
    const raw = String(data.cell.raw ?? '');
    if (raw === '—' || !raw.includes(';')) return;
    const [latStr, lngStr] = raw.split(';');
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (!isFinite(lat) || !isFinite(lng)) return;
    doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
      url: `https://www.google.com/maps?q=${lat},${lng}`,
    });
  };
}

function finalY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable
    .finalY;
}

const TABLE = {
  styles: { fontSize: 9, cellPadding: 2 },
  headStyles: {
    fillColor: [230, 230, 230] as [number, number, number],
    textColor: 0 as number,
    fontStyle: 'bold' as const,
  },
  theme: 'grid' as const,
};

const GEO_COLOR = { textColor: [22, 119, 255] as [number, number, number] };

function sectionHeader(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(text, 14, y);
  return y + 4;
}

export function exportCollectionRemito(
  detail: CollectionDetail,
  shipments: CollectionShipment[],
  options?: { storeId?: number },
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const M = 14;
  let y = M;

  const byStore = options?.storeId != null;
  const targetStore = byStore
    ? detail.stores.find((s) => s.storeId === options!.storeId)
    : null;

  // ── title ──
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(
    byStore && targetStore
      ? `Remito Colecta #${detail.collectionRequestId} — ${targetStore.storeName}`
      : `Remito Colecta #${detail.collectionRequestId}`,
    M,
    y,
  );
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Creado: ${fmt(detail.createdAt)}   Estado: ${STATUS_LABELS[detail.status] ?? detail.status}`,
    M,
    y,
  );
  doc.setTextColor(0, 0, 0);
  y += 8;

  // ── driver ──
  y = sectionHeader(doc, 'Driver', y);
  autoTable(doc, {
    ...TABLE,
    startY: y,
    margin: { left: M, right: M },
    head: [['Nombre', 'DNI', 'Patente']],
    body: [
      [
        detail.driverName || '—',
        detail.driverDni || '—',
        detail.vehiclePlate || '—',
      ],
    ],
  });
  y = finalY(doc) + 6;

  // ── deposit ──
  if (detail.depositName) {
    y = sectionHeader(doc, 'Depósito', y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Nombre', 'Dirección', 'Ubicación']],
      body: [
        [
          detail.depositName,
          detail.depositAddress || '—',
          geoText(detail.depositLat, detail.depositLng),
        ],
      ],
      columnStyles: { 2: GEO_COLOR },
      didDrawCell: geoLinkHook(doc, 2),
    });
    y = finalY(doc) + 6;
  }

  if (byStore && targetStore) {
    // ── per-store mode ──

    y = sectionHeader(doc, 'Tienda', y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [
        [
          'Nombre',
          'Marketplace',
          'Estado',
          'Recogida',
          'Dirección',
          'Ubicación',
        ],
      ],
      body: [
        [
          targetStore.storeName,
          targetStore.marketplaceName,
          STATUS_LABELS[targetStore.status] ?? targetStore.status,
          targetStore.collectedAt ? fmtShort(targetStore.collectedAt) : '—',
          targetStore.storeAddress || '—',
          geoText(targetStore.storeLat, targetStore.storeLng),
        ],
      ],
      columnStyles: { 5: GEO_COLOR },
      didDrawCell: geoLinkHook(doc, 5),
    });
    y = finalY(doc) + 6;

    type TRow = {
      occurred_at: string;
      type: string;
      detail: string;
      time: string;
      geo: string;
    };
    const timelineRows: TRow[] = [
      ...detail.geofenceEvents
        .filter(
          (ev) =>
            ev.locationType === 'store' && ev.storeId === targetStore.storeId,
        )
        .map((ev) => ({
          occurred_at: ev.occurredAt,
          type: EVENT_TYPE_LABELS[ev.eventType] ?? ev.eventType,
          detail: '—',
          time: fmtShort(ev.occurredAt),
          geo: geoText(ev.lat, ev.lng),
        })),
      ...detail.pickupLogs
        .filter((pl) => pl.storeId === targetStore.storeId)
        .map((pl) => ({
          occurred_at: pl.occurredAt,
          type: 'Recolección',
          detail: String(pl.packageCount),
          time: fmtShort(pl.occurredAt),
          geo: geoText(pl.lat, pl.lng),
        })),
    ].sort(
      (a, b) =>
        new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
    );

    if (timelineRows.length > 0) {
      y = sectionHeader(doc, 'Línea de tiempo', y);
      autoTable(doc, {
        ...TABLE,
        startY: y,
        margin: { left: M, right: M },
        head: [['Evento', 'Paquetes', 'Hora', 'Ubicación']],
        body: timelineRows.map((r) => [r.type, r.detail, r.time, r.geo]),
        columnStyles: { 3: GEO_COLOR },
        didDrawCell: geoLinkHook(doc, 3),
      });
      y = finalY(doc) + 6;
    }

    const storeShipments = shipments.filter(
      (s) => s.collection_request_store_id === targetStore.storeId,
    );
    y = sectionHeader(doc, `Envíos (${storeShipments.length})`, y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Código externo', 'Fecha']],
      body:
        storeShipments.length > 0
          ? storeShipments.map((s) => [s.external_code, fmtShort(s.created_at)])
          : [['Sin envíos', '—']],
    });
  } else {
    // ── general mode ──

    y = sectionHeader(doc, 'Tiendas', y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Tienda', 'MP', 'Estado', 'Recogida', 'Dirección', 'Ubicación']],
      body: detail.stores.map((s) => [
        s.storeName,
        s.marketplaceName,
        STATUS_LABELS[s.status] ?? s.status,
        s.collectedAt ? fmtShort(s.collectedAt) : '—',
        s.storeAddress || '—',
        geoText(s.storeLat, s.storeLng),
      ]),
      columnStyles: { 5: GEO_COLOR },
      didDrawCell: geoLinkHook(doc, 5),
    });
    y = finalY(doc) + 6;

    const hasEvents =
      detail.geofenceEvents.length > 0 || detail.pickupLogs.length > 0;
    if (hasEvents) {
      const storeMap = Object.fromEntries(
        detail.stores.map((s) => [s.storeId, s.storeName]),
      );
      type TRow = {
        occurred_at: string;
        store: string;
        type: string;
        detail: string;
        time: string;
        geo: string;
      };
      const timelineRows: TRow[] = [
        ...detail.geofenceEvents.map((ev) => ({
          occurred_at: ev.occurredAt,
          store:
            ev.locationType === 'store'
              ? ev.storeId != null
                ? (storeMap[ev.storeId] ?? 'Tienda')
                : 'Tienda'
              : ev.locationType === 'deposit'
                ? 'Depósito'
                : ev.locationType,
          type: EVENT_TYPE_LABELS[ev.eventType] ?? ev.eventType,
          detail: '—',
          time: fmtShort(ev.occurredAt),
          geo: geoText(ev.lat, ev.lng),
        })),
        ...detail.pickupLogs.map((pl) => ({
          occurred_at: pl.occurredAt,
          store: pl.storeId != null ? (storeMap[pl.storeId] ?? '—') : '—',
          type: 'Recolección',
          detail: String(pl.packageCount),
          time: fmtShort(pl.occurredAt),
          geo: geoText(pl.lat, pl.lng),
        })),
      ].sort(
        (a, b) =>
          new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
      );

      y = sectionHeader(doc, 'Línea de tiempo', y);
      autoTable(doc, {
        ...TABLE,
        startY: y,
        margin: { left: M, right: M },
        head: [['Tienda', 'Evento', 'Paquetes', 'Hora', 'Ubicación']],
        body: timelineRows.map((r) => [
          r.store,
          r.type,
          r.detail,
          r.time,
          r.geo,
        ]),
        columnStyles: { 4: GEO_COLOR },
        didDrawCell: geoLinkHook(doc, 4),
      });
      y = finalY(doc) + 6;
    }

    const storeMap2 = Object.fromEntries(
      detail.stores.map((s) => [String(s.storeId), s.storeName]),
    );
    y = sectionHeader(doc, `Envíos (${shipments.length})`, y);
    autoTable(doc, {
      ...TABLE,
      startY: y,
      margin: { left: M, right: M },
      head: [['Tienda', 'Código externo', 'Fecha']],
      body:
        shipments.length > 0
          ? shipments.map((s) => [
              storeMap2[s.collection_request_store_id] ??
                s.collection_request_store_id,
              s.external_code,
              fmtShort(s.created_at),
            ])
          : [['Sin envíos', '—', '—']],
    });
  }

  const filename =
    byStore && targetStore
      ? `remito-colecta-${detail.collectionRequestId}-${targetStore.storeName.replace(/\s+/g, '-').toLowerCase()}.pdf`
      : `remito-colecta-${detail.collectionRequestId}.pdf`;
  doc.save(filename);
}
